<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-email</v-icon>
        Invite Details
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            v-if="invite?.status !== 'Client Completed'"
            color="info"
            dark
            rounded
            size="large"
            @click="resendInvite"
            :loading="actionLoading"
            :disabled="actionLoading"
            class="mr-2"
          >
            <v-icon left>mdi-email-send</v-icon>
            Resend Invite
          </v-btn>
          
          <v-btn
            color="error"
            dark
            rounded
            size="large"
            @click="deleteInvite"
            :loading="actionLoading"
            :disabled="actionLoading"
            class="mr-2"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete Invite
          </v-btn>
          
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <v-alert
          v-if="error"
          type="error"
          class="mb-4"
          variant="tonal"
        >
          {{ error }}
        </v-alert>

        <v-skeleton-loader
          v-if="loading"
          type="article, article"
          class="mb-6"
        />

        <div v-if="!loading && !notFound">
          <!-- Invite Header -->
          <div class="text-center mb-6">
            <v-avatar size="80" color="#002060" class="mb-3">
              <v-icon size="40" color="white">mdi-email</v-icon>
            </v-avatar>
            <h2 class="invite-name mb-2">{{ displayName }}</h2>
            <p class="invite-email mb-3">{{ invite?.email || '—' }}</p>
            <p v-if="invite?.idNumber" class="invite-id mb-2">ID: {{ formattedIdNumber }}</p>
            <v-chip :color="statusColor" text-color="white" size="large">
              {{ invite?.status || 'Pending' }}
            </v-chip>
            <p v-if="invite?.status === 'Client Completed'" class="text-success mt-2">
              <v-icon small>mdi-check-circle</v-icon>
              This invite has been completed
            </p>
          </div>

          <!-- For Pending Invites - Simple View -->
          <v-row v-if="!isCompletedInvite">
            <v-col cols="12">
              <h3 class="section-title mb-4">Basic Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="displayName"
                label="Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.email || '—'"
                label="Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="formattedPhone"
                label="Cellphone Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.memberType || '—'"
                label="Member Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-multiple"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.membershipType || '—'"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                readonly
              />
            </v-col>
          </v-row>

          <!-- For Completed Invites - Detailed View -->
          <v-row v-if="isCompletedInvite">
            <v-col cols="12">
              <h3 class="section-title mb-4">Basic Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.firstName || '—'"
                label="First Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.surname || '—'"
                label="Last Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-outline"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="formattedIdNumber"
                label="ID Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="formattedPhone"
                label="Cellphone Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.email || '—'"
                label="Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.confirmEmail || '—'"
                label="Confirm Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email-check"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.memberType || '—'"
                label="Member Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-multiple"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.membershipType || '—'"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                readonly
              />
            </v-col>
          </v-row>

          <!-- Address Information - Only for Completed Invites -->
          <v-row v-if="isCompletedInvite">
            <v-col cols="12">
              <h3 class="section-title mb-4">Address Information</h3>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                :model-value="invite?.addressLine1 || '—'"
                label="Street Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.addressLine2 || '—'"
                label="Apartment/Suite"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home-outline"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.city || '—'"
                label="City"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-city"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.postalCode || '—'"
                label="Postal Code"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-mailbox"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.address || '—'"
                label="Full Address"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-map-marker"
                readonly
              />
            </v-col>
          </v-row>

          <!-- Banking Information - Only for Completed Invites -->
          <v-row v-if="isCompletedInvite">
            <v-col cols="12">
              <h3 class="section-title mb-4">Banking Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.bankName || '—'"
                label="Bank Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-bank"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.bankAccountNumber || '—'"
                label="Account Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-credit-card"
                readonly
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- System Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">System Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.dateAdded || '—'"
                label="Date Added"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-calendar"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="invite?.completedAt ? new Date(invite.completedAt).toLocaleString() : '—'"
                label="Completed At"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-check-circle"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6" v-if="isCompletedInvite">
              <v-text-field
                :model-value="invite?.termsAccepted ? 'Yes' : 'No'"
                label="Terms Accepted"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-check-circle-outline"
                readonly
              />
            </v-col>
            
            <v-col cols="12" md="6" v-if="isCompletedInvite">
              <v-text-field
                :model-value="invite?.clientId || invite?.memberId || '—'"
                label="Client/Member ID"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-identifier"
                readonly
              />
            </v-col>
          </v-row>

          <v-row v-if="invite?.notes">
            <!-- Additional Notes -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Additional Notes</h3>
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                :model-value="invite.notes"
                label="Notes"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-note-text"
                rows="3"
                readonly
              />
            </v-col>
          </v-row>
        </div>
        
        <div v-if="!loading && notFound" class="text-center py-8">
          <v-icon size="64" color="#ccc">mdi-email-search</v-icon>
          <h3 class="mt-2" style="color:#666; font-weight:600;">Invite not found</h3>
          <p class="text-medium-emphasis">The selected invite could not be found. It may have been removed.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatIdNumber } from '../utils/idNumberFormatter';
import { formatPhoneNumber } from '../utils/phoneFormatter';

export default {
  name: 'InviteDetails',
  components: {
    NotificationDialog
  },
  props: {
    id: { type: String, required: true }
  },
  data() {
    return {
      invite: null,
      loading: true,
      actionLoading: false,
      error: null,
      notFound: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: ''
    };
  },
  computed: {
    displayName() {
      if (!this.invite) return '—';
      if (this.invite.memberType === 'Business') {
        return this.invite.businessName || '—';
      }
      const first = this.invite.firstName || '';
      const last = this.invite.surname || '';
      return `${first} ${last}`.trim() || '—';
    },
    isCompletedInvite() {
      return this.invite?.status === 'Client Completed' || 
             this.invite?.completedAt || 
             this.invite?.idNumber ||
             this.invite?.addressLine1;
    },
    statusColor() {
      const s = (this.invite?.status || '').toLowerCase();
      if (s === 'client completed') return 'success';
      return 'warning';
    },
    formattedIdNumber() {
      return formatIdNumber(this.invite?.idNumber) || '—';
    },
    formattedPhone() {
      return formatPhoneNumber(this.invite?.phone) || '—';
    }
  },
  async created() {
    try {
      if (!this.id) {
        this.error = 'No invite ID provided';
        this.notFound = true;
        return;
      }

      console.log('Loading invite with ID:', this.id);
      
      // First try to find in pendingInvites collection
      let ref = doc(db, 'pendingInvites', this.id);
      let snap = await getDoc(ref);
      
      if (snap.exists()) {
        this.invite = { id: snap.id, ...snap.data() };
        console.log('Invite loaded from pendingInvites:', this.invite);
      } else {
        // If not found in pendingInvites, try completedInvites collection
        console.log('Invite not found in pendingInvites, checking completedInvites...');
        ref = doc(db, 'completedInvites', this.id);
        snap = await getDoc(ref);
        
        if (snap.exists()) {
          this.invite = { id: snap.id, ...snap.data() };
          console.log('Invite loaded from completedInvites:', this.invite);
        } else {
          // If still not found, try to find by originalInviteId in completedInvites
          console.log('Invite not found by ID, searching by originalInviteId...');
          const completedRef = collection(db, 'completedInvites');
          const q = query(completedRef, where('originalInviteId', '==', this.id));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            this.invite = { id: doc.id, ...doc.data() };
            console.log('Invite found by originalInviteId in completedInvites:', this.invite);
          } else {
            console.error('Invite document not found in any collection:', this.id);
            this.notFound = true;
            this.error = 'This invite may have been completed or deleted.';
          }
        }
      }
    } catch (e) {
      this.error = 'Failed to load invite details';
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async resendInvite() {
      try {
        this.actionLoading = true;

        if (!this.invite || !this.invite.email) {
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'No email available for this invite.';
          this.showNotification = true;
          return;
        }

        // Prevent resending if already completed
        if (this.isCompletedInvite || (this.invite.status && this.invite.status === 'Client Completed')) {
          this.notificationType = 'info';
          this.notificationTitle = 'Already Completed';
          this.notificationMessage = 'This registration is already completed. No resend necessary.';
          this.showNotification = true;
          return;
        }

        const recipientFullName = (this.invite.memberType === 'Business')
          ? (this.invite.businessName || 'Member')
          : `${this.invite.firstName || ''} ${this.invite.surname || ''}`.trim() || 'Member';

        const response = await fetch(
          'https://sendinviteemail-zjdy2tr2aa-uc.a.run.app',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: this.invite.email,
              subject: 'Welcome to the DMM Law Club - Your Invitation',
              recipientFullName,
              referredByName: this.invite.referredByName || '',
              companyName: this.invite.companyName || ''
            })
          }
        );

        if (!response.ok) {
          const text = await response.text();
          console.error('Resend failed:', response.status, text);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to resend invite. Please try again.';
          this.showNotification = true;
          return;
        }

        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Invite resent successfully!';
        this.showNotification = true;
      } catch (e) {
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to resend invite. Please try again.';
        this.showNotification = true;
        console.error(e);
      } finally {
        this.actionLoading = false;
      }
    },
    async deleteInvite() {
      try {
        this.actionLoading = true;
        
        if (!this.invite || !this.invite.id) {
          throw new Error('No invite data available for deletion');
        }
        
        console.log('Attempting to delete invite:', this.invite);
        console.log('Is completed invite:', this.isCompletedInvite);
        
        // For completed invites, we need to delete from multiple collections
        if (this.isCompletedInvite) {
          console.log('Deleting completed invite from multiple collections...');
          
          // Delete from completedInvites collection
          try {
            await deleteDoc(doc(db, 'completedInvites', this.invite.id));
            console.log('Deleted from completedInvites:', this.invite.id);
          } catch (error) {
            console.error('Error deleting from completedInvites:', error);
          }
          
          // Also try to delete from newMembers collection if it exists
          if (this.invite.memberId) {
            try {
              await deleteDoc(doc(db, 'newMembers', this.invite.memberId));
              console.log('Deleted from newMembers:', this.invite.memberId);
            } catch (error) {
              console.error('Error deleting from newMembers:', error);
            }
          }
          
          // Try to delete by originalInviteId if different from current id
          if (this.invite.originalInviteId && this.invite.originalInviteId !== this.invite.id) {
            try {
              await deleteDoc(doc(db, 'completedInvites', this.invite.originalInviteId));
              console.log('Deleted by originalInviteId:', this.invite.originalInviteId);
            } catch (error) {
              console.error('Error deleting by originalInviteId:', error);
            }
          }
          
        } else {
          // For pending invites, just delete from pendingInvites
          console.log('Deleting pending invite from pendingInvites...');
          await deleteDoc(doc(db, 'pendingInvites', this.invite.id));
          console.log('Deleted from pendingInvites:', this.invite.id);
        }
        
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Invite deleted successfully!';
        this.showNotification = true;
        setTimeout(() => {
          this.$router.back();
        }, 1500);
      } catch (e) {
        console.error('Error deleting invite:', e);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to delete invite. Please try again.';
        this.showNotification = true;
      } finally {
        this.actionLoading = false;
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.invite-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.invite-email {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.invite-id {
  font-size: 0.875rem;
  color: #888;
  margin: 0;
  font-weight: 400;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style>


